import { useEffect, useLayoutEffect, useState } from 'react'

import {
  addDoc,
  auth,
  collection,
  deleteDoc,
  doc,
  firestore,
  onIdTokenChanged,
  onSnapshot,
  setDoc,
  updateDoc,
} from '@/helpers/firebase'
import { consoleTimeTrack } from '../consoleTimeTrack'
import { setUser } from '@utsubo/experience'

export async function createDocument(ref, data) {
  return await addDoc(collection(firestore, ref), data)
}

export async function getDocument(ref) {
  //https://github.com/firebase/firebase-js-sdk/issues/6739#issuecomment-1330233573
  //https://firebase.google.com/docs/firestore/query-data/listen
  const data = await new Promise((resolve) => {
    const unsub = onSnapshot(
      doc(firestore, ref),
      { includeMetadataChanges: true },
      async (snapshot) => {
        const source = snapshot.metadata.hasPendingWrites ? 'Local' : 'Server'
        if (source === 'Server') {
          unsub()
          resolve(snapshot.exists() ? snapshot.data() : null)
        }
      }
    )
  })
  return data
}

export async function setDocument(ref, data) {
  await setDoc(doc(firestore, ref), data)
}

/* will merge both properties and nested properties */
export async function updateDocument(ref, data) {
  await setDoc(doc(firestore, ref), data, { merge: true })
}

/* will leave root doc properties untouched and fully replace other props and their nested content */
export async function updateDocumentField(ref, data) {
  await updateDoc(doc(firestore, ref), data, { merge: true })
}

export async function deleteDocument(ref) {
  await deleteDoc(doc(firestore, ref))
}

async function deleteCookie() {
  await fetch(`/api/deleteauthcookie`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}
async function setIdToken(user) {
  const idTokenResult = await user.getIdTokenResult()
  const idToken = idTokenResult.token

  const previewId = idToken ? idToken.slice(0, 10) : 'no token'
  consoleTimeTrack(
    `setidtoken ${previewId}, will send decodedToken through backendLogin `,
    { fileName: 'firebase/api.js', methodName: 'setIdToken' }
  )

  await fetch(`/api/setidtoken`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
    },
  })
}

/**
 * Gets the current signed-in user.
 */
export function useUser() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  useEffect(() => {
    onIdTokenChanged(auth, async (user) => {
      if (user) {
        await setIdToken(user)

        setUser({
          refreshToken: user.getIdToken
        })
      } else {
        await deleteCookie()
      }
      // emitEvent('onIdTokenChanged', user)
      setData(user)

      setLoading(false)
    })
    return () => {}
  }, [])

  return { loadingForTokenChange: loading, data }
}

export const unwrapSnap = (snap) =>
  snap.exists() ? { docId: snap.id, ...snap.data() } : null

export function useDocument(ref) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  useLayoutEffect(() => {
    let loading = true
    return onSnapshot(doc(firestore, ref), (snap) => {
      if (loading) setLoading((loading = false))
      setData(unwrapSnap(snap))
    })
  }, [ref])

  return { loading, data }
}

export function useCollection(ref) {
  const [loading, setLoading] = useState(true)
  const [arr] = useState([])
  const [data, setData] = useState(() => arr)
  const [, setForceRemount] = useState(false)

  useLayoutEffect(() => {
    let loading = true
    arr.length = 0
    setLoading(loading)

    return onSnapshot(collection(firestore, ref), (snap) => {
      if (loading) setLoading((loading = false))
      const changes = snap.docChanges()
      for (const change of changes) {
        if (change.oldIndex !== -1) arr.splice(change.oldIndex, 1)
        if (change.newIndex !== -1) {
          arr.splice(change.newIndex, 0, unwrapSnap(change.doc))
        }
      }
      setData(arr)
      setForceRemount((value) => !value)
    })
  }, [arr, ref])

  return { loading, data }
}
